import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    
    const msg = error?.message || "";
    
    // Auto-recover from Google Translate DOM mutation errors
    const isTranslateError =
      msg.includes("removeChild") ||
      msg.includes("insertBefore") ||
      msg.includes("appendChild") ||
      msg.includes("NotFoundError") ||
      msg.includes("Failed to execute") ||
      msg.includes("The node to be removed is not a child");
    
    // Auto-recover from chunk/module loading errors (stale deployments)
    const isChunkError =
      msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("Loading chunk") ||
      msg.includes("Loading CSS chunk") ||
      msg.includes("error loading dynamically imported module") ||
      msg.includes("Importing a module script failed") ||
      msg.includes("Unable to preload CSS");
    
    if (isTranslateError) {
      console.warn("ErrorBoundary: suppressing likely Google Translate DOM error, auto-recovering");
      setTimeout(() => this.setState({ hasError: false }), 100);
      return;
    }
    
    if (isChunkError) {
      console.warn("ErrorBoundary: stale chunk detected, reloading page");
      // Force reload to get fresh chunks — but only once to avoid infinite loops
      const lastReload = sessionStorage.getItem("eb_last_reload");
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload) > 10000) {
        sessionStorage.setItem("eb_last_reload", now.toString());
        window.location.reload();
        return;
      }
    }

    // For any other error, auto-recover after a short delay to avoid permanent error screens
    setTimeout(() => this.setState({ hasError: false }), 2000);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "40px", textAlign: "center", fontFamily: "system-ui, sans-serif" }}>
          <h1 style={{ fontSize: "24px", marginBottom: "16px" }}>Something went wrong</h1>
          <p style={{ color: "#666", marginBottom: "24px" }}>Please refresh the page to continue.</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "12px 24px",
              backgroundColor: "#2d7a3a",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
